import React, { useState, useEffect } from "react";
import {
  Autocomplete,
  Avatar,
  TextField,
  useMediaQuery,
  CircularProgress,
  Dialog,
  Snackbar,
  Alert,
} from "@mui/material";
import Label from "../../Common/Label";
import ChannelCard from "../ChannelCard";
import CalanderIcon from "../../../assets/images/Calender.svg";
import DollerIcon from "../../../assets/images/Doller.svg";
import LinkIcon from "../../../assets/images/Link.svg";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { postRequest } from "../../../api/apiServices";
import apiList from "../../../api/apiList";
import "../../../styles/Form2.css";
import "../../../styles/dialog-premium.css";

import {
  filterByOptions,
  sortByAllTypes,
  sortByOthers,
} from "../../../constant/VonqConstant";

interface Form2Props {
  nextStep: () => void;
  prevStep: () => void;
}

interface ChannelItem {
  logo: string;
  channelName: string;
  channelType: string;
  title: string;
  homepage: string;
  price: number;
  duration: string;
  processingTime: string;
  locations: string[];
  industries: string[];
  productId: string;
  description: string;
}

interface ApiResponse {
  success: boolean;
  statusCode: number;
  statusMessage: string;
  data: {
    label: string;
    items: ChannelItem[];
  }[];
}

interface ApiRequestPayload {
  filterBy: string;
  sortBy: string;
  offset: number;
  includeLocationId: number;
  jobFunctionId: number;
  industryId?: number;
}

const getFilterApiValue = (displayValue: string): string => {
  const mapping: { [key: string]: string } = {
    "All Types": "all",
    "Bundle Only": "bundles",
    "Free Add-on Only": "free_addons",
  };
  return mapping[displayValue] || displayValue;
};

const getSortApiValue = (displayValue: string): string => {
  const mapping: { [key: string]: string } = {
    "Recommended First": "recommended",
    Relevant: "relevant",
    "High Price": "list_price.desc",
    "Low Price": "list_price.asc",
  };
  return mapping[displayValue] || displayValue;
};

const getCurrentFilterLabel = (option: string): string => {
  const filterOption = filterByOptions.find((item) => item.value === option);
  return filterOption ? filterOption.label : option;
};

const Form2: React.FC<Form2Props> = ({ nextStep, prevStep }) => {
  const [selectedSort, setSelectedSort] = useState<string>("Recommended First");
  const [selectedFilter, setSelectedFilter] = useState<string>("All Types");
  const [channelData, setChannelData] = useState<{
    recommended: ChannelItem[];
    allExceptRecommended: ChannelItem[];
    freeAddons: ChannelItem[];
  }>({
    recommended: [],
    allExceptRecommended: [],
    freeAddons: [],
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState<number>(0);
  const [isFetchingMore, setIsFetchingMore] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);

  const [infoOpen, setInfoOpen] = useState<boolean>(false);
  const [infoItem, setInfoItem] = useState<ChannelItem | null>(null);

  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>("");

  const [selectedCampaignsMap, setSelectedCampaignsMap] = useState<
    Map<string, any>
  >(new Map());

  const isMobile = useMediaQuery("(max-width: 600px)");
  const PAGE_SIZE = 20;
  const loadMoreRef = React.useRef<HTMLDivElement | null>(null);
  const scrollContainerRef = React.useRef<HTMLDivElement | null>(null);

  const getCurrentSortOptions = () => {
    return selectedFilter === "All Types" ? sortByAllTypes : sortByOthers;
  };

  const handleAddCampaign = (product: any) => {
    const id = product?.productId || product?.id;
    if (!id) return;

    // Validation: Check if the product is already part of a selected bundle
    // or if a selected product is part of this new bundle
    for (const [existingId, existingProduct] of Array.from(
      selectedCampaignsMap.entries()
    )) {
      // Case 1: New product is in an existing bundle
      if (
        existingProduct.bundle_products_ids &&
        existingProduct.bundle_products_ids.includes(id)
      ) {
        setSnackbarMessage(
          `This product is already included in the "${existingProduct.title}" bundle.`
        );
        setSnackbarOpen(true);
        return false;
      }

      // Case 2: Existing product is in the new bundle
      if (
        product.bundle_products_ids &&
        product.bundle_products_ids.includes(existingId)
      ) {
        setSnackbarMessage(
          `You have already selected "${existingProduct.title}" which is part of this bundle.`
        );
        setSnackbarOpen(true);
        return false;
      }

      // Case 3: Both are bundles and share common products
      if (
        existingProduct.bundle_products_ids &&
        product.bundle_products_ids &&
        existingProduct.bundle_products_ids.length > 0 &&
        product.bundle_products_ids.length > 0
      ) {
        const intersection = existingProduct.bundle_products_ids.filter(
          (bundleProductId: string) =>
            product.bundle_products_ids.includes(bundleProductId)
        );

        if (intersection.length > 0) {
          setSnackbarMessage(
            `The "${product.title}" bundle shares products with the already selected "${existingProduct.title}" bundle.`
          );
          setSnackbarOpen(true);
          return false;
        }
      }
    }

    const campaignWithContext = {
      ...product,
      context: {
        createdAt: new Date().toISOString(),
      },
    };

    const newMap = new Map(selectedCampaignsMap);
    newMap.set(id, campaignWithContext);
    setSelectedCampaignsMap(newMap);
    updateLocalStorageWithSelection(Array.from(newMap.values()));
    return true;
  };

  const handleRemoveCampaign = (product: any) => {
    const id = product?.productId || product?.id;
    if (!id) return;

    const newMap = new Map(selectedCampaignsMap);
    newMap.delete(id);
    setSelectedCampaignsMap(newMap);
    updateLocalStorageWithSelection(Array.from(newMap.values()));
  };

  const handleOpenInfo = (item: ChannelItem) => {
    setInfoItem(item);
    setInfoOpen(true);
  };

  const handleCloseInfo = () => {
    setInfoOpen(false);
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const updateLocalStorageWithSelection = (products: any[]) => {
    const storedData = localStorage.getItem("CompaignData");
    let parsedData = {};

    if (storedData && storedData !== "undefined" && storedData !== "null") {
      try {
        parsedData = JSON.parse(storedData);
      } catch (e) {
        parsedData = {};
      }
    }

    const updatedData = {
      ...parsedData,
      selectedCampaign: products,
      selectedCampaigns: products,
    };

    localStorage.setItem("CompaignData", JSON.stringify(updatedData));
  };

  const loadSelectedCampaigns = () => {
    const storedData = localStorage.getItem("CompaignData");
    if (!storedData) return;

    try {
      const parsedData = JSON.parse(storedData);

      const campaigns: any[] =
        (Array.isArray(parsedData.selectedCampaigns)
          ? parsedData.selectedCampaigns
          : Array.isArray(parsedData.selectedCampaign)
          ? parsedData.selectedCampaign
          : []) || [];

      if (campaigns.length === 0) return;

      const map = new Map<string, any>();
      campaigns.forEach((c) => {
        const id = c?.productId ?? c?.id;
        if (id) map.set(id, c);
      });

      setSelectedCampaignsMap(map);
    } catch (e) {}
  };

  // Helper function to process API data based on filter
  const processApiData = (data: ApiResponse["data"], filterValue: string) => {
    const findItemsByLabel = (label: string) =>
      data.find((item) => item.label === label)?.items || [];

    switch (filterValue) {
      case "All Types":
        return {
          recommended: findItemsByLabel("recommended"),
          allExceptRecommended: findItemsByLabel("all_except_recommended"),
          freeAddons: findItemsByLabel("free_addons"),
        };

      case "Bundle Only":
        return {
          recommended: [],
          allExceptRecommended: findItemsByLabel("bundles"),
          freeAddons: [],
        };

      case "Free Add-on Only":
        return {
          recommended: [],
          allExceptRecommended: [],
          freeAddons: findItemsByLabel("free_addons"),
        };

      default:
        return {
          recommended: [],
          allExceptRecommended: data[0]?.items || [],
          freeAddons: [],
        };
    }
  };

  const fetchChannelData = async (
    filterValue: string,
    sortValue: string,
    requestOffset: number = 0,
    append: boolean = false
  ) => {
    append ? setIsFetchingMore(true) : setLoading(true);
    setError(null);

    const payload: ApiRequestPayload & { limit?: number } = {
      filterBy: getFilterApiValue(filterValue),
      sortBy: getSortApiValue(sortValue),
      offset: requestOffset,
      limit: PAGE_SIZE,
      includeLocationId: 78383,
      jobFunctionId: 31,
      industryId: 1,
    };

    const apiEndpoint = apiList.getFilterBy.replace(
      "/campaigns/gateway/",
      "/gateway/"
    );

    try {
      const response = await postRequest(apiEndpoint, payload);
      if (!response?.data) {
        throw new Error("No data received from API");
      }
      const data: ApiResponse = response.data;
      if (!data.success || data.statusCode !== 200) {
        throw new Error(data.statusMessage || "API request failed");
      }
      const processedData = processApiData(data.data, filterValue);
      const receivedCount =
        processedData.recommended.length +
        processedData.allExceptRecommended.length +
        processedData.freeAddons.length;

      if (append) {
        setChannelData((prev) => ({
          recommended: [...prev.recommended, ...processedData.recommended],
          allExceptRecommended: [
            ...prev.allExceptRecommended,
            ...processedData.allExceptRecommended,
          ],
          freeAddons: [...prev.freeAddons, ...processedData.freeAddons],
        }));
      } else {
        setChannelData(processedData);
      }

      // Continue loading while the API keeps returning any items
      setHasMore(receivedCount > 0);
    } catch (err: any) {
      setError("An error occurred while fetching data");
    } finally {
      append ? setIsFetchingMore(false) : setLoading(false);
    }
  };

  const handleFilterChange = (event: any, newValue: string | null) => {
    if (newValue) {
      setSelectedFilter(newValue);

      const newSortOptions =
        newValue === "All Types" ? sortByAllTypes : sortByOthers;
      const newSortValue = newSortOptions[0];
      setSelectedSort(newSortValue);
      setOffset(0);
      setHasMore(true);
      fetchChannelData(newValue, newSortValue, 0, false);
    }
  };

  const handleSortChange = (event: any, newValue: string | null) => {
    if (newValue) {
      setSelectedSort(newValue);
      setOffset(0);
      setHasMore(true);
      fetchChannelData(selectedFilter, newValue, 0, false);
    }
  };

  const handleLoadMore = () => {
    if (loading || isFetchingMore || !hasMore) return;
    const nextOffset = offset + PAGE_SIZE;
    setOffset(nextOffset);
    fetchChannelData(selectedFilter, selectedSort, nextOffset, true);
  };

  useEffect(() => {
    setOffset(0);
    setHasMore(true);
    fetchChannelData(selectedFilter, selectedSort, 0, false);
    loadSelectedCampaigns();
  }, []);

  // Auto-load when the sentinel becomes visible
  useEffect(() => {
    if (!loadMoreRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first.isIntersecting) {
          handleLoadMore();
        }
      },
      { root: scrollContainerRef.current, rootMargin: "0px", threshold: 0.1 }
    );
    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [
    loadMoreRef.current,
    loading,
    isFetchingMore,
    hasMore,
    offset,
    selectedFilter,
    selectedSort,
    scrollContainerRef.current,
  ]);

  return (
    <>
      <div className="container" id="campaign-container">
        <div className="row flex-column gap-3 g-0">
          <div className="col d-flex align-items-start justify-content-center flex-column gap-1">
            <div className="d-flex">
              <Avatar className="avatar">2</Avatar>
              <Label
                children="Media Mix Recommendation"
                fontSize={"20.94px"}
                fontWeight={700}
                color="#273848"
              />
            </div>
            <Label
              children="We will use this information to recommend you the best performing channels, so you can target the right candidates."
              fontWeight={500}
              fontSize={"12.94px"}
              color="#273848"
            />
          </div>

          {/* Filter */}
          <div className="col d-flex align-items-center justify-content-start justify-content-sm-end flex-column flex-md-row gap-3">
            <div>
              <Label
                children="Sort By"
                fontWeight={500}
                fontSize={"16px"}
                color="#4E4E4E"
              />
              <Autocomplete
                options={getCurrentSortOptions()}
                value={selectedSort}
                onChange={handleSortChange}
                disabled={loading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    size="small"
                    fullWidth
                    sx={{
                      width: "100px",
                      minWidth: "250px",
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {/* {loading ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null} */}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </div>
            <div>
              <Label
                children="Filter By"
                fontWeight={500}
                fontSize={"16px"}
                color="#4E4E4E"
              />
              <Autocomplete
                options={filterByOptions.map((option) => option.value)}
                getOptionLabel={(option) => getCurrentFilterLabel(option)}
                value={selectedFilter}
                onChange={handleFilterChange}
                disabled={loading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    size="small"
                    fullWidth
                    sx={{
                      width: "100px",
                      minWidth: "250px",
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {/* {loading ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null} */}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </div>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="col-12 text-center py-4">
              <CircularProgress size={40} />
              <Label
                children="Loading channels..."
                fontWeight={500}
                fontSize={"14px"}
                color="#4E4E4E"
                className="mt-2"
              />
            </div>
          )}

          {/* Error State */}
          {error && !loading && (
            <div className="col-12 text-center py-3">
              <Label
                children={`Error: ${error}`}
                fontWeight={500}
                fontSize={"14px"}
                color="#dc3545"
                className="mb-2"
              />
            </div>
          )}

          <div className="col-12 channels-scroll" ref={scrollContainerRef}>
            {/* Recommended Channels */}
            {!loading && !error && channelData.recommended.length > 0 && (
              <div className="col-12 mt-2">
                <Label
                  children="Recommended Channel"
                  fontWeight={700}
                  fontSize={"14.94px"}
                  color="#273848"
                  className="mb-2"
                />
                <div className="row gx-4 gy-3">
                  {channelData.recommended.map((item, index) => {
                    const pid = (item as any).productId || (item as any).id;
                    return (
                      <ChannelCard
                        key={`recommended-${pid || index}`}
                        duration={item.duration}
                        price={String(item.price)}
                        jobType={item.channelType}
                        jobTitle={item.title}
                        websiteLink={item.homepage}
                        logo={item.logo}
                        productId={pid}
                        isSelected={!!(pid && selectedCampaignsMap.has(pid))}
                        calendarIcon={CalanderIcon}
                        dollarIcon={DollerIcon}
                        linkIcon={LinkIcon}
                        onAddCampaign={() => handleAddCampaign(item)}
                        onRemoveCampaign={() => handleRemoveCampaign(item)}
                        onMoreInfo={() => handleOpenInfo(item)}
                      />
                    );
                  })}
                </div>
              </div>
            )}

            {/* All Except Recommended Channels */}
            {!loading &&
              !error &&
              channelData.allExceptRecommended.length > 0 && (
                <div className="col-12 mt-2">
                  <Label
                    children="All Except Recommended"
                    fontWeight={700}
                    fontSize={"14.94px"}
                    color="#273848"
                    className="mb-2"
                  />
                  <div className="row gx-4 gy-3">
                    {channelData.allExceptRecommended.map((item, index) => {
                      const pid = (item as any).productId || (item as any).id;
                      return (
                        <ChannelCard
                          key={`all-except-recommended-${pid || index}`}
                          duration={item.duration}
                          price={String(item.price)}
                          jobType={item.channelType}
                          jobTitle={item.title}
                          websiteLink={item.homepage}
                          logo={item.logo}
                          productId={pid}
                          isSelected={!!(pid && selectedCampaignsMap.has(pid))}
                          calendarIcon={CalanderIcon}
                          dollarIcon={DollerIcon}
                          linkIcon={LinkIcon}
                          onAddCampaign={() => handleAddCampaign(item)}
                          onRemoveCampaign={() => handleRemoveCampaign(item)}
                          onMoreInfo={() => handleOpenInfo(item)}
                        />
                      );
                    })}
                  </div>
                </div>
              )}

            {/* Free Add-ons Channels */}
            {!loading && !error && channelData.freeAddons.length > 0 && (
              <div className="col-12 mt-2">
                <Label
                  children="Free Add-ons"
                  fontWeight={700}
                  fontSize={"14.94px"}
                  color="#273848"
                  className="mb-2"
                />
                <div className="row gx-4 gy-3">
                  {channelData.freeAddons.map((item, index) => {
                    const pid = (item as any).productId || (item as any).id;
                    return (
                      <ChannelCard
                        key={`free-addons-${pid || index}`}
                        duration={item.duration}
                        price={String(item.price)}
                        jobType={item.channelType}
                        jobTitle={item.title}
                        websiteLink={item.homepage}
                        logo={item.logo}
                        productId={pid}
                        isSelected={!!(pid && selectedCampaignsMap.has(pid))}
                        calendarIcon={CalanderIcon}
                        dollarIcon={DollerIcon}
                        linkIcon={LinkIcon}
                        onAddCampaign={() => handleAddCampaign(item)}
                        onRemoveCampaign={() => handleRemoveCampaign(item)}
                        onMoreInfo={() => handleOpenInfo(item)}
                      />
                    );
                  })}
                </div>
              </div>
            )}

            {/* Infinite Load Sentinel */}
            <div ref={loadMoreRef} className="mt-3" style={{ height: 1 }} />

            {isFetchingMore && (
              <div className="col-12 text-center py-3">
                <CircularProgress size={24} />
              </div>
            )}
          </div>

          <hr className="mt-5" />
          {/* Button Section */}
          <div className="col d-flex flex-column flex-sm-row align-items-center justify-content-between gap-3">
            <button
              className="previous-btn w-100"
              onClick={prevStep}
              disabled={loading}
            >
              {isMobile
                ? "Previous"
                : "Previous - Let's Target the right talent"}
            </button>
            <button
              className="continue-btn w-100"
              onClick={nextStep}
              disabled={loading}
            >
              Next - Target Group
            </button>
          </div>
        </div>
      </div>

      {/* Info Dialog */}
      <Dialog
        open={infoOpen}
        onClose={handleCloseInfo}
        maxWidth="sm"
        fullWidth
        className="premium-dialog-root"
        BackdropProps={{ className: "premium-modal-backdrop" }}
        PaperProps={{
          elevation: 0,
          className: "premium-dialog-paper premium-dialog-paper--wide",
        }}
      >
        {infoItem && (
          <>
            <div className="premium-dialog-header">
              <h2 className="premium-dialog-title">{infoItem.title}</h2>
              <button
                type="button"
                className="premium-dialog-close"
                onClick={handleCloseInfo}
                aria-label="Close"
              >
                <CloseRoundedIcon sx={{ fontSize: 18 }} />
              </button>
            </div>

            <div className="premium-dialog-body">
              {infoItem.logo && (
                <img
                  src={infoItem.logo}
                  alt={infoItem.title || "channel logo"}
                  className="premium-dialog-channel-logo"
                />
              )}
              <p className="premium-dialog-detail">{infoItem.description}</p>
              <p className="premium-dialog-detail">
                <strong>Price:</strong> ${infoItem.price}
              </p>
              <p className="premium-dialog-detail">
                <strong>Duration:</strong> {infoItem.duration}
              </p>
              <p className="premium-dialog-detail">
                <strong>Processing Time:</strong> {infoItem.processingTime}
              </p>
              <p className="premium-dialog-detail">
                <strong>Locations:</strong> {infoItem.locations.join(", ")}
              </p>
            </div>

            <div className="premium-dialog-footer">
              <button
                type="button"
                className="premium-dialog-btn premium-dialog-btn--primary"
                onClick={handleCloseInfo}
              >
                Close
              </button>
            </div>
          </>
        )}
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="warning"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Form2;
